import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Mail, Lock, User, Building2, ArrowRight, ShieldCheck } from "lucide-react";
import { Toaster, toast } from "sonner";
import api, { readError } from "./api";

export default function Setup() {
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [fernetOk, setFernetOk] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company_name: "", name: "Yönetici", email: "", password: "", confirm: "",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/setup/status");
        setNeedsSetup(!!data.needs_setup);
        setFernetOk(!!data.fernet_configured);
        if (!data.needs_setup) {
          nav("/login", { replace: true });
        }
      } catch {
        setNeedsSetup(false);
        nav("/login", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const next = () => {
    if (step === 1 && !form.company_name.trim()) return toast.error("Şirket adı gerekli");
    if (step === 2) {
      if (!form.email.trim() || !form.email.includes("@")) return toast.error("Geçerli e-posta girin");
      if (form.password.length < 8) return toast.error("Şifre en az 8 karakter olmalı");
      if (form.password !== form.confirm) return toast.error("Şifreler eşleşmiyor");
    }
    setStep(s => s + 1);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/setup/init", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim() || "Yönetici",
        company_name: form.company_name.trim(),
      });
      toast.success("Kurulum tamamlandı. Şimdi giriş yapabilirsiniz.");
      setTimeout(() => nav("/login", { replace: true }), 800);
    } catch (err) {
      toast.error(readError(err, "Kurulum başarısız"));
    } finally { setBusy(false); }
  };

  if (checking) return <div className="fullscreen-loading">Kurulum kontrol ediliyor…</div>;
  if (!needsSetup) return null;

  return (
    <div className="login-wrap">
      <div className="login-card setup-card" data-testid="setup-card" style={{ maxWidth: 520 }}>
        <div className="login-brand">
          <div className="login-mark"><Sparkles size={22} /></div>
          <h1>Hoş geldiniz</h1>
          <p>Teklif Stüdyosu ilk kurulumu · Adım {step} / 3</p>
        </div>

        {!fernetOk && (
          <div className="revision-banner" style={{ margin: "12px 0" }} data-testid="setup-fernet-warn">
            <b>⚠ FERNET_KEY tanımlı değil</b>
            <span>SMTP şifresi ve Resend API anahtarları düz metin saklanır. Üretim için <code>backend/.env</code> içine <code>FERNET_KEY</code> ekleyip servisi yeniden başlatın.</span>
          </div>
        )}

        <div className="setup-step">
          {step === 1 && (
            <>
              <label>
                <Building2 size={14} /> Şirket adı
                <input data-testid="setup-company" autoFocus placeholder="Ör. CDS Teknoloji"
                  value={form.company_name} onChange={e => setField("company_name", e.target.value)} />
              </label>
              <p className="hint">Teklif başlığında ve müşteri e-postalarında gösterilir.</p>
              <button className="primary-button" onClick={next} data-testid="setup-next-1">
                Devam <ArrowRight size={15} />
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <label>
                <User size={14} /> Yönetici adı
                <input data-testid="setup-name" value={form.name}
                  onChange={e => setField("name", e.target.value)} placeholder="Ad Soyad" />
              </label>
              <label>
                <Mail size={14} /> Yönetici e-postası
                <input data-testid="setup-email" type="email" autoComplete="username"
                  value={form.email} onChange={e => setField("email", e.target.value)}
                  placeholder="admin@sirket.com" />
              </label>
              <label>
                <Lock size={14} /> Şifre (min 8 karakter)
                <input data-testid="setup-password" type="password" autoComplete="new-password"
                  value={form.password} onChange={e => setField("password", e.target.value)} />
              </label>
              <label>
                <Lock size={14} /> Şifre (tekrar)
                <input data-testid="setup-confirm" type="password" autoComplete="new-password"
                  value={form.confirm} onChange={e => setField("confirm", e.target.value)} />
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="outline-button" onClick={() => setStep(1)}>Geri</button>
                <button className="primary-button" onClick={next} data-testid="setup-next-2" style={{ flex: 1 }}>
                  Devam <ArrowRight size={15} />
                </button>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="setup-review" data-testid="setup-review">
                <div><span>Şirket</span><b>{form.company_name}</b></div>
                <div><span>Yönetici</span><b>{form.name}</b></div>
                <div><span>E-posta</span><b>{form.email}</b></div>
                <div><span>Şifre uzunluğu</span><b>{form.password.length} karakter</b></div>
                <div><span>Şifreleme (Fernet)</span>
                  <b style={{ color: fernetOk ? "#059669" : "#dc2626" }}>
                    {fernetOk ? "Etkin" : "Devre dışı"}
                  </b>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="outline-button" onClick={() => setStep(2)}>Geri</button>
                <button className="primary-button" onClick={submit} disabled={busy}
                  data-testid="setup-submit" style={{ flex: 1 }}>
                  <ShieldCheck size={15} /> {busy ? "Kaydediliyor..." : "Kurulumu tamamla"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}
